import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaHome, FaQuestionCircle, FaUser } from "react-icons/fa";
import axios from "axios";
import Cookies from "js-cookie";
import authStore from "../utils/authStorage";
import LeftInformationPanel from "./LeftInformationPanel";
import "../styles/TopNavBar.css";

export default function TopNavBar({ onToggleSidebar }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [showHelp, setShowHelp] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const accountRef = useRef(null);

  const isLoggedIn = !!localStorage.getItem("token");
  const isAdmin = localStorage.getItem("userRole") === "1";
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const isApplyPage = location.pathname.startsWith("/apply/");
  const leaveApplyMessage =
    "Leave this application and return to the job list? You can choose a different position there.";

  // close account dropdown when clicking outside
  useEffect(() => {
    function onDocClick(e) {
      if (!accountRef.current) return;
      if (!accountRef.current.contains(e.target)) setAccountOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Profile completeness check
  useEffect(() => {
    if (!isLoggedIn) return;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.get(`${BASE_URL}/api/apply/check`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIsOnboarding(data.pipeline_status === 2);
      } catch {
        setIsOnboarding(false);
      }
    })();
  }, [isLoggedIn, BASE_URL]);

  // hide navbar during onboarding
  if (isLoggedIn && isOnboarding) return null;

  /* =====================================================
     NOT LOGGED IN (THIS IS WHERE WE CHANGE THINGS)
     ===================================================== */
  if (!isLoggedIn) {
    return (
      <>
        {/* ===== TOP BAR ===== */}
        <header className="topnav" role="banner">
          {/* LEFT: hamburger (mobile only) */}
          <div className="topnav-side topnav-left">
            <button
              type="button"
              className="topnav-icon mobile-only"
              onClick={() => setMobileDrawerOpen(true)}
              aria-label="Menu"
              title="Menu"
            >
              <span className="topnav-icon-glyph">≡</span>
            </button>
          </div>

          {/* CENTER: logo */}
          <div className="topnav-center" aria-hidden="true">
            <img
              src="/logoMain.png"
              alt="New Way Trucking"
              className="topnav-logo"
            />
          </div>

          {/* RIGHT: continue button (DESKTOP ONLY) */}
          <div className="topnav-side topnav-right desktop-only">
            <button
              type="button"
              className="topnav-icon"
              onClick={() => navigate("/login")}
              aria-label="Continue an application"
            >
              <span className="topnav-icon-label">
                Continue an application ?
              </span>
            </button>
          </div>
        </header>

        {/* ===== MOBILE DRAWER OVERLAY ===== */}
        <div
          className={`mobile-drawer-overlay ${mobileDrawerOpen ? "show" : ""}`}
          onClick={() => setMobileDrawerOpen(false)}
        />

        {/* ===== MOBILE DRAWER ===== */}
        <aside className={`mobile-drawer ${mobileDrawerOpen ? "show" : ""}`}>
          <div className="mobile-drawer-header">
            <button
              className="drawer-close"
              onClick={() => setMobileDrawerOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* CONTINUE BUTTON (TOP, LEFT-ALIGNED) */}
          <div className="drawer-continue">
            <button
              type="button"
              className="topnav-icon drawer-continue-btn"
              onClick={() => navigate("/login")}
              aria-label="Continue an application"
            >
              <span className="topnav-icon-label">
                Continue an application ?
              </span>
            </button>
          </div>

          {/* LEFT PANEL CONTENT */}
          <div className="drawer-left-info">
            <LeftInformationPanel />
          </div>
        </aside>
      </>
    );
  }

  /* =====================================================
     LOGGED IN NAVBAR (UNCHANGED)
     ===================================================== */
  const goHome = () => {
    if (isApplyPage) {
      const confirmed = window.confirm(leaveApplyMessage);
      if (!confirmed) return;

      const token = localStorage.getItem("token");
      Cookies.remove("pendingJobId");

      if (token) {
        axios
          .delete(`${BASE_URL}/api/apply/selection`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .catch((err) => {
            console.error("Failed to clear selected job:", err);
          });
      }
    }

    if (isAdmin) return navigate("/admin");
    navigate("/");
  };

  const handleLogout = () => {
    authStore.clearAll();
    navigate("/login", { replace: true });
  };

  return (
    <>
      <header className="topnav" role="banner">
        <div className="topnav-side topnav-left">
          {isAdmin && onToggleSidebar && (
            <button
              type="button"
              className="topnav-icon"
              onClick={onToggleSidebar}
              aria-label="Menu"
            >
              <span className="topnav-icon-glyph">≡</span>
              <span className="topnav-icon-label">Menu</span>
            </button>
          )}

          <button
            type="button"
            className="topnav-icon"
            onClick={goHome}
            aria-label="Home"
          >
            <span className="topnav-icon-glyph">
              <FaHome />
            </span>
            <span className="topnav-icon-label">Home</span>
          </button>
        </div>

        <div className="topnav-center" aria-hidden="true">
          <img
            src="/logoMain.png"
            alt="New Way Trucking"
            className="topnav-logo"
          />
        </div>

        <div className="topnav-side topnav-right">
          <button
            type="button"
            className="topnav-icon"
            onClick={() => setShowHelp(true)}
            aria-label="Help"
          >
            <span className="topnav-icon-glyph">
              <FaQuestionCircle />
            </span>
            <span className="topnav-icon-label">Help</span>
          </button>

          <div className="topnav-dropdown" ref={accountRef}>
            <button
              type="button"
              className="topnav-icon"
              onClick={() => setAccountOpen((v) => !v)}
              aria-haspopup="menu"
            >
              <span className="topnav-icon-glyph">
                <FaUser />
              </span>
              <span className="topnav-icon-label">My Account</span>
            </button>

            {accountOpen && (
              <div className="topnav-menu">
                <button
                  className="topnav-menu-item"
                  onClick={() => {
                    setAccountOpen(false);
                    navigate("/profile");
                  }}
                >
                  Profile
                </button>

                {isAdmin && (
                  <button
                    className="topnav-menu-item"
                    onClick={() => {
                      setAccountOpen(false);
                      navigate("/admin");
                    }}
                  >
                    Admin
                  </button>
                )}

                <div className="topnav-menu-sep" />

                <button
                  className="topnav-menu-item danger"
                  onClick={handleLogout}
                >
                  🔓 Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {showHelp && (
        <div
          className="topnav-modal-backdrop"
          onClick={() => setShowHelp(false)}
        >
          <div className="topnav-modal" onClick={(e) => e.stopPropagation()}>
            <div className="topnav-modal-head">
              <strong>💬 Support</strong>
              <button
                className="topnav-btn-close"
                onClick={() => setShowHelp(false)}
              >
                ×
              </button>
            </div>
            <div className="topnav-modal-body">
              <p>
                For application questions contact <b>New-Way Trucking Ltd</b>:
                <br />
                <a href="tel:4035904393">403-590-4393</a> •{" "}
                <a href="mailto:hr@newwaytrucking.com">hr@newwaytrucking.com</a>
              </p>
            </div>
            <div className="topnav-modal-foot">
              <button
                className="btn btn-primary"
                onClick={() => setShowHelp(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
